// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useMemo, useState } from 'react';
import { Image, Platform, Pressable, ScrollView, StyleSheet, TextInput } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { Redirect, useRootNavigationState, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { InviteQrScannerModal } from '@/components/InviteQrScannerModal';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { ResumeClaimCard } from '@/components/ResumeClaimCard';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { useI18n } from '@/i18n';
import { useUser } from './user-context';
import { getAssetImageSource } from '@/services/assetImageSource';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { parseInviteQrPayload } from '@/services/inviteQrPayload';
import { resolveTourByInviteCode } from '@/services/tourInviteLink';
import { buildForgotPasswordUrl, loginWithPassword, REDIRECT_URI } from '@/auth/oidc';
import { useRoundState } from '@/stores/roundState';
import appColors from '@/theme/colors';

const START_HERO_IMAGE_OPTIONS = {
  width: 1200,
  height: 474,
  bucketStep: 1,
} as const;

export default function Index() {
  const { t } = useI18n();
  const { user, setUser } = useUser();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const loginHeroImage = useMemo(
    () => getAssetImageSource('images/login-hero.png', '', '/', START_HERO_IMAGE_OPTIONS),
    [],
  );
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeError, setInviteCodeError] = useState<string | null>(null);
  const [joiningWithCode, setJoiningWithCode] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const { handleRedirect } = useAuthRedirect({ user, setUser });

  const handlePasswordLogin = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setAuthError(t('start.credentialsRequired'));
      return;
    }

    setAuthError(null);
    setLoggingIn(true);
    try {
      const auth = await loginWithPassword(trimmedEmail, password);
      useRoundState.getState().clear();
      setUser({
        id: auth.id,
        forename: auth.name,
        token: auth.accessToken,
        idToken: auth.idToken ?? null,
        refreshToken: auth.refreshToken ?? null,
      });
      router.replace('/welcome');
    } catch {
      setAuthError(t('start.loginFailed'));
    } finally {
      setLoggingIn(false);
    }
  };

  const handleForgotPassword = async () => {
    const url = buildForgotPasswordUrl();
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
      window.location.href = url;
      return;
    }
    await WebBrowser.openAuthSessionAsync(url, REDIRECT_URI);
  };

  const joinWithToken = useCallback(async (inviteToken: string) => {
    setInviteCodeError(null);
    setJoiningWithCode(true);
    try {
      const resolvedInvite = await resolveTourByInviteCode({
        apiBaseUrl,
        inviteToken,
      });

      router.push({
        pathname: '/tour/invite/[inviteToken]',
        params: {
          inviteToken: resolvedInvite.inviteToken,
          tourId: resolvedInvite.tourId,
          targetType: resolvedInvite.targetType ?? 'tour',
          roundId: resolvedInvite.roundId,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setInviteCodeError(
        message.includes('invalidInviteToken')
          ? t('start.roundCodeInvalid')
          : t('start.roundCodeLookupFailed')
      );
    } finally {
      setJoiningWithCode(false);
    }
  }, [apiBaseUrl, router, t]);

  const handleJoinWithCode = () => {
    const normalizedCode = inviteCode.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase();
    if (!normalizedCode) {
      setInviteCodeError(t('start.roundCodeRequired'));
      return;
    }
    void joinWithToken(normalizedCode);
  };

  /*
   * A scanned Goffy QR carries the full share link, whose token is longer than the typeable
   * join code — so it goes to the BFF verbatim rather than through the field's normalisation.
   * It is still mirrored into the field so a failed lookup shows what was actually read.
   */
  const handleQrScanned = (raw: string) => {
    const scan = parseInviteQrPayload(raw);
    setScannerOpen(false);

    if (scan.kind === 'friendInvite') {
      setInviteCodeError(t('start.qrScanFriendInvite'));
      return;
    }
    if (scan.kind !== 'invite') {
      setInviteCodeError(t('start.qrScanUnrecognized'));
      return;
    }

    setInviteCode(scan.inviteToken);
    void joinWithToken(scan.inviteToken);
  };

  if (!isNavigationReady) {
    return null;
  }

  if (user) {
    return <Redirect href="/welcome" />;
  }

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.container}
      keyboardShouldPersistTaps="handled"
    >
      <Image source={loginHeroImage} style={styles.hero} resizeMode="contain" />
      <Text style={styles.title}>{t('start.title')}</Text>
      <Text style={styles.subtitle}>{t('start.subtitle')}</Text>
      <LanguageSwitcher />

      {/* Renders only when this device holds an active claim, so a guest who scanned a
          QR code can get back to their round without signing in. */}
      <ResumeClaimCard />

      <View style={styles.card}>
        <Text style={styles.fieldLabel}>{t('start.roundCodeLabel')}</Text>
        <TextInput
          value={inviteCode}
          onChangeText={(next) => {
            setInviteCode(next);
            if (inviteCodeError) {
              setInviteCodeError(null);
            }
          }}
          placeholder={t('start.roundCodePlaceholder')}
          placeholderTextColor="#8FA08B"
          autoCapitalize="characters"
          autoCorrect={false}
          style={styles.input}
          onSubmitEditing={handleJoinWithCode}
          returnKeyType="go"
        />
        {inviteCodeError ? <Text style={styles.errorText}>{inviteCodeError}</Text> : null}
        <GoffyButton
          label={t('start.joinWithCodeCta')}
          onPress={handleJoinWithCode}
          disabled={joiningWithCode}
          tone="dark"
          style={styles.button}
        />
        <GoffyButton
          label={t('start.scanQrCta')}
          onPress={() => {
            setInviteCodeError(null);
            setScannerOpen(true);
          }}
          disabled={joiningWithCode}
          tone="dark"
          style={styles.button}
          testID="scan-invite-qr-button"
        />

        <View style={styles.divider}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>{t('start.orDivider')}</Text>
          <View style={styles.dividerLine} />
        </View>

        <Text style={styles.fieldLabel}>{t('start.emailLabel')}</Text>
        <TextInput
          value={email}
          onChangeText={(next) => {
            setEmail(next);
            if (authError) {
              setAuthError(null);
            }
          }}
          placeholder={t('start.emailPlaceholder')}
          placeholderTextColor="#8FA08B"
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          textContentType="emailAddress"
          style={styles.input}
        />
        <Text style={styles.fieldLabel}>{t('start.passwordLabel')}</Text>
        <TextInput
          value={password}
          onChangeText={(next) => {
            setPassword(next);
            if (authError) {
              setAuthError(null);
            }
          }}
          placeholder={t('start.passwordPlaceholder')}
          placeholderTextColor="#8FA08B"
          autoCapitalize="none"
          autoCorrect={false}
          secureTextEntry
          textContentType="password"
          style={styles.input}
          onSubmitEditing={handlePasswordLogin}
          returnKeyType="go"
        />
        {authError ? <Text style={styles.errorText}>{authError}</Text> : null}
        <Pressable
          style={styles.forgotLink}
          onPress={() => {
            void handleForgotPassword();
          }}
        >
          <Text style={styles.forgotLinkText}>{t('start.forgotPasswordCta')}</Text>
        </Pressable>
        <GoffyButton
          label={loggingIn ? t('auth.loggingIn') : t('start.logInCta')}
          onPress={() => {
            void handlePasswordLogin();
          }}
          disabled={loggingIn}
          tone="accent"
          style={styles.button}
        />
        <GoogleSignInButton
          label={t('start.googleCta')}
          onPress={() => {
            void handleRedirect('google');
          }}
          style={styles.button}
        />
        <GoffyButton
          label={t('start.signUpCta')}
          onPress={() => router.push('/register')}
          tone="dark"
          style={styles.button}
        />
      </View>

      <InviteQrScannerModal
        visible={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanned={handleQrScanned}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: '#011806',
  },
  container: {
    // flexGrow (not flex) so the content still centres on tall screens but is
    // allowed to overflow — and scroll — when it doesn't fit.
    flexGrow: 1,
    padding: 24,
    gap: 16,
    justifyContent: 'center',
    backgroundColor: '#011806',
  },
  hero: {
    width: '100%',
    maxWidth: 720,
    aspectRatio: 1200 / 474,
    alignSelf: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: '800',
    textAlign: 'center',
    color: appColors.text.primary,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    color: '#A1B49E',
    maxWidth: 420,
    alignSelf: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 460,
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    padding: 16,
    gap: 10,
  },
  fieldLabel: {
    fontSize: 12,
    letterSpacing: 2.2,
    textTransform: 'uppercase',
    fontWeight: '700',
    color: '#8FA08B',
  },
  input: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: appColors.text.primary,
    backgroundColor: '#0C2510',
  },
  errorText: {
    color: '#FF7A7A',
    fontSize: 13,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginVertical: 4,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#1A3A20',
  },
  dividerText: {
    color: '#8FA08B',
    fontSize: 12,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  forgotLink: {
    alignSelf: 'flex-end',
    paddingVertical: 2,
  },
  forgotLinkText: {
    color: '#A1B49E',
    fontSize: 13,
    textDecorationLine: 'underline',
  },
  button: {
    width: '100%',
  },
});
